import { useEffect, useState } from 'react'
import { Search, Filter } from 'lucide-react'
import { api } from '../lib/api'
import {
  NUDGE_LIBRARY_CATEGORIES,
  NudgeCard,
  NudgePreviewModal,
  type LibraryNudge,
} from '../components/nudges'

export function NudgeLibraryPage() {
  const [nudges, setNudges] = useState<LibraryNudge[]>([])
  const [loading, setLoading] = useState(true)
  const [category, setCategory] = useState<string>('all')
  const [search, setSearch] = useState('')
  const [preview, setPreview] = useState<LibraryNudge | null>(null)

  useEffect(() => {
    setLoading(true)
    const params: Record<string, string> = { limit: '200' }
    if (category !== 'all') params.category = category

    api
      .getNudges(params)
      .then((r) => setNudges((r as { nudges: LibraryNudge[] }).nudges))
      .finally(() => setLoading(false))
  }, [category])

  const filtered = nudges.filter(
    (n) =>
      !search ||
      n.title.toLowerCase().includes(search.toLowerCase()) ||
      n.body.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <div className="mb-7">
        <h1 className="text-2xl font-bold text-ink" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
          Nudge Library
        </h1>
        <p className="text-sm mt-1" style={{ color: '#6b8a8f' }}>
          Browse and preview science-backed behavioral prompts
        </p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft" />
          <input
            type="text"
            placeholder="Search nudges…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-warm-card border border-warm-border rounded-xl pl-9 pr-4 py-2.5 text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:border-brand-teal transition-colors shadow-card"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {NUDGE_LIBRARY_CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() => setCategory(cat)}
              className="px-3 py-1.5 rounded-full text-xs font-medium capitalize transition-colors border"
              style={
                category === cat
                  ? {
                      background: '#2A7A8A',
                      color: '#fff',
                      borderColor: '#2A7A8A',
                      boxShadow: '0 0 20px -6px rgba(42,122,138,0.4)',
                    }
                  : {
                      background: '#FDFCF8',
                      color: '#6b8a8f',
                      borderColor: '#e0d9cc',
                    }
              }
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      <p className="text-xs text-ink-soft mb-4 flex items-center gap-1.5">
        <Filter size={12} />
        {filtered.length} nudge{filtered.length !== 1 ? 's' : ''}
      </p>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="bg-warm-card border border-warm-border rounded-2xl p-5 h-36 animate-pulse shadow-card" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {filtered.map((nudge) => (
            <NudgeCard key={nudge.id} nudge={nudge} onClick={() => setPreview(nudge)} />
          ))}
          {filtered.length === 0 && (
            <div className="col-span-2 text-center py-16 text-ink-muted text-sm">No nudges match your search.</div>
          )}
        </div>
      )}

      {preview && <NudgePreviewModal nudge={preview} onClose={() => setPreview(null)} />}
    </div>
  )
}
